import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import React from 'react';
import { Loading } from '@components/atoms/Loading';
import { EmptyData } from '@components/molecules/EmptyData';
import { Project } from '@models/project.model';
import { ClientProjectOverview } from '@models/client.model';
import { useTranslation } from '@hooks/useTranslation';
import {
  ClientAttentionBlock,
  ClientAttentionKind,
} from './ClientAttentionBlock';
import { ClientProjectCard } from './ClientProjectCard';
import { fillTemplate } from './clientProjectDisplay';

interface ClientProjectsRailProps {
  projects: Project[];
  isLoading?: boolean;
  /** `rail`: a vertical list beside the feed. `grid`: full width when there is no feed column. */
  layout: 'rail' | 'grid';
  organizationName: string;
  onProjectClick: (projectId: string) => void;
  /** Sprint 44: the server's progress rows, in the same order as `projects`. */
  overviews?: ClientProjectOverview[];
  overviewsLoading?: boolean;
  overviewsError?: boolean;
  onAttentionNavigate?: (projectId: string, kind: ClientAttentionKind) => void;
}

/**
 * Sprint 43, story-223. The CLIENT's projects, every state. Never mounts a star, a geofence
 * control, or a create-project empty state: an empty list says the contractor has not added the
 * client to a project yet.
 */
export const ClientProjectsRail: React.FC<ClientProjectsRailProps> = ({
  projects,
  isLoading = false,
  layout,
  organizationName,
  onProjectClick,
  overviews = [],
  overviewsLoading = false,
  overviewsError = false,
  onAttentionNavigate,
}) => {
  const tDashboard = useTranslation('dashboard');
  const tProgress = useTranslation('clientProgress');
  const overviewById = new Map(overviews.map((o) => [o.projectId, o]));
  const orderedOverviews = projects
    .map((p) => (p.projectId ? overviewById.get(p.projectId) : undefined))
    .filter((o): o is ClientProjectOverview => !!o);
  const isRail = layout === 'rail';

  return (
    <section
      className={
        isRail
          ? 'flex flex-col pb-2 min-h-0 h-[calc(100vh-3rem)]'
          : 'flex flex-col pb-2'
      }>
      {!overviewsLoading && !overviewsError && onAttentionNavigate && (
        <ClientAttentionBlock
          overviews={orderedOverviews}
          onNavigate={onAttentionNavigate}
        />
      )}
      <div className="flex items-center ml-4 gap-4 flex-shrink-0 pt-1">
        <WorkOutlineOutlinedIcon
          sx={{ fontSize: 28 }}
          className="text-orange-400 dark:text-orange-300"
        />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          {tDashboard.clientYourProjects}
        </h2>
      </div>

      {overviewsError && (
        <p
          data-testid="client-overview-unavailable"
          className="ml-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
          {tProgress.unavailable}
        </p>
      )}

      <div
        className={
          isRail
            ? 'py-2 pl-4 flex flex-col gap-2 mt-8 flex-1 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
            : 'py-2 pl-4 mt-8 grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2'
        }>
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loading size="xl" />
          </div>
        ) : projects.length > 0 ? (
          projects.map((project) => (
            <ClientProjectCard
              key={project.projectId}
              project={project}
              onClick={onProjectClick}
              overview={
                project.projectId
                  ? overviewById.get(project.projectId)
                  : undefined
              }
              overviewLoading={overviewsLoading}
            />
          ))
        ) : (
          <div className="col-span-full">
            <EmptyData
              description={fillTemplate(tDashboard.clientNoProjects, {
                org: organizationName,
              })}
            />
          </div>
        )}
      </div>
    </section>
  );
};
